// Resource Gantt — 페이지
//
// 필터(version / resource / period)는 공용 필터 컴포넌트를 재사용한다.
// 데이터는 GET /api/resource-gantt에서 version/resource/period 기준으로 조회
// (세그먼트=z_mp_tracking, capacity=z_resource_util, 버킷=버전 horizon).

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Slider from "@mui/material/Slider";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import HighlightAltIcon from "@mui/icons-material/HighlightAlt";
import HeightIcon from "@mui/icons-material/Height";
import CheckIcon from "@mui/icons-material/Check";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HideSourceIcon from "@mui/icons-material/HideSource";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import { StyledToggleButton, ToggleButtonGroup } from "@plannel/components/ui/ToggleButton";
import { ResetButton, SettingsButton } from "@plannel/components/ActionIconButton";

import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import CodeUtil from "@plannel/pages/utils/code-util";
import WorkbenchUtil from "@plannel/pages/demand-plan/dp-workbench/WorkbenchUtil";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import MpVersionFilter from "@plannel/components/filter/MpVersionFilter";
import ResourceFilter from "@plannel/components/filter/ResourceFilter";
import PeriodFilter from "@plannel/components/filter/PeriodFilter";
import { MPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";

import debug from "@plannel/utils/debug-util";
import resourceGanttService from "@plannel/services/mp/resource-gantt-service";
import hrchyConfigService from "@plannel/services/data/hrchy-config-service";
import ResourceGanttCanvas from "@plannel/pages/master-plan/resource-gantt/ResourceGanttCanvas";
import OrderSpecPanel from "@plannel/pages/master-plan/resource-gantt/OrderSpecPanel";
import ResourceUtilChart from "@plannel/pages/master-plan/resource-gantt/ResourceUtilChart";
import ResourceUtilTable from "@plannel/pages/master-plan/resource-gantt/ResourceUtilTable";
import { alignBuckets, bucketLimitCap, bucketUsed, buildItemColor } from "@plannel/pages/master-plan/resource-gantt/ganttScales";
import {
  ITEM_PALETTES,
  DEFAULT_PALETTE,
  getPaletteSetupColor,
  LOAD_COLORS,
  DEFAULT_LOAD_THRESHOLDS,
  makeLoadColor,
  isOverloaded,
  ORDER_PANEL_WIDTH_DEFAULT,
  BORDER_COLOR,
} from "@plannel/pages/master-plan/resource-gantt/ganttConstants";

const PALETTE_LABEL_KEY = { classic: "paletteClassic", nord: "paletteNord", muted: "paletteMuted", dusty: "paletteDusty" };

// 버킷(열) 너비 슬라이더 — 값은 '화면을 채우는 버킷 수'라 적을수록 넓다.
// 1 = 한 버킷이 차트 폭을 다 쓰는 최대 너비(행 높이 슬라이더 최댓값과 비슷한 스케일), 36 = 최소 너비.
// 슬라이더는 '너비'이므로 오른쪽=넓게가 되도록 개수를 반전 매핑한다(함수가 곧 역함수).
const BUCKET_COUNT_MIN = 1;
const BUCKET_COUNT_MAX = 36;
const flipBucketCount = (v) => BUCKET_COUNT_MIN + BUCKET_COUNT_MAX - v;
const EMPTY_GANTT = { resources: [], bucketsByResource: {}, bucketKeys: [], bucketLabels: {} };

// 기간 기본값 — 최근 1년(이번 달 말까지), 월 단위. (ZDate는 전역)
const CURRENT_DATE = new ZDate();
const INITIAL_START_DATE = new ZDate(CURRENT_DATE.getFullYear() - 1, CURRENT_DATE.getMonth() + 1, 1);
const INITIAL_END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() + 1, 0);
const INITIAL_PERIOD = { start: INITIAL_START_DATE, end: INITIAL_END_DATE };

const ResourceGantt = ({ viewName }) => {
  const { t } = useTranslation();
  const reduxDispatch = useDispatch();
  const { isPermitted } = useMenuAccess();   // 권한 밖 화면으로 가는 우클릭 메뉴는 숨긴다
  const reduxViewState = reduxUtil.getViewState(viewName);

  // 필터 상태 — redux view-state에서 복원(각 필터 컴포넌트가 동일 키로 저장)
  const [version, setVersion] = useState(reduxViewState?.mpVersion ?? null);
  const [resource, setResource] = useState(reduxViewState?.resource ?? []); // 다중선택
  // PeriodFilter는 첫 렌더에서 period.start/end를 직접 읽으므로 null이 아닌 객체로 초기화한다.
  const [period, setPeriod] = useState(
    reduxViewState?.period
      ? { start: new ZDate(reduxViewState.period.start), end: new ZDate(reduxViewState.period.end) }
      : INITIAL_PERIOD
  );

  // Enter/검색 아이콘 시 호출 — 아이템 코드 적용(BOM 자식 포함 필터)
  // getViewState 가 숫자형 코드("12345")를 숫자로 파싱하므로 문자열로 되돌린다(ItemAutocomplete 는 문자열 전제).
  const itemSearch = useRef(reduxViewState?.itemSearch ? String(reduxViewState.itemSearch) : null); // 검색된 아이템 코드(검색 결과가 없으면 null)

  const [colorMode, setColorMode] = useState(reduxViewState?.colorMode ?? "item");
  const [rowHeight, setRowHeight] = useState(reduxViewState?.rowHeight ?? 518); // 행(버킷) 높이 — 기본값 518px(240~1320)
  const [bucketCount, setBucketCount] = useState(reduxViewState?.bucketCount ?? 21); // 화면을 채우는 버킷 수(=열 너비, 많을수록 좁음. BUCKET_COUNT_MIN~MAX)
  const [displayBucket, setDisplayBucket] = useState(reduxViewState?.displayBucket ?? null); // 표시 버킷 집계(null=버전 기본 / week / month)
  const [hrchyLvCd, setHrchyLvCd] = useState(reduxViewState?.hrchyLvCd ?? null); // 세그먼트를 품목 계층 '레벨'로 집계(null=품목별, 값=z_hrchy_config.cd)
  const [hrchyLevels, setHrchyLevels] = useState([]); // 품목 계층 레벨 목록 [{cd, name}] — 드롭다운 옵션(hrchyNum=1 그룹)
  const groupByHrchy = hrchyLvCd != null; // 계층 집계 모드 여부(메뉴 노출/PSI 이동 분기)
  // 저장된 palette가 사라진 키(옛 cool/warm)면 기본으로 폴백
  const [palette, setPalette] = useState(
    ITEM_PALETTES[reduxViewState?.palette] ? reduxViewState.palette : DEFAULT_PALETTE
  ); // 색상 팔레트
  const [stackDir, setStackDir] = useState(reduxViewState?.stackDir ?? "vertical"); // 스택 방향
  const [timeUom, setTimeUom] = useState(reduxViewState?.timeUom ?? "seconds"); // 용량 표시 단위(ms→s/m/h)
  const [loadThresholds, setLoadThresholds] = useState(reduxViewState?.loadThresholds ?? DEFAULT_LOAD_THRESHOLDS); // 부하 색 임계값(%)
  // 초과 여백 제거(100%면 버킷 꽉 참) — 기본 ON. 이미 저장된 뷰 상태가 있으면 그 선택을 존중한다.
  const [fitCapacity, setFitCapacity] = useState(reduxViewState?.fitCapacity ?? true);
  const [panelWidth, setPanelWidth] = useState(reduxViewState?.panelWidth ?? ORDER_PANEL_WIDTH_DEFAULT); // 주문 사양 패널 폭(스플리터)
  const [settingsAnchor, setSettingsAnchor] = useState(null); // ⚙ 설정 메뉴 앵커
  const [highlightOrder, setHighlightOrder] = useState(null); // '같은 주문 강조' 모드의 orderNo
  const [overloadOnly, setOverloadOnly] = useState(reduxViewState?.overloadOnly ?? false); // 초과(>capacity) 리소스만 보기
  const [loadedOnly, setLoadedOnly] = useState(reduxViewState?.loadedOnly ?? false); // 부하가 전혀 없는(미가동) 리소스 숨기기
  const [showSetup, setShowSetup] = useState(reduxViewState?.showSetup ?? true); // 세그먼트 내 setup(작업 준비) 구간 표시
  const [selectedOrder, setSelectedOrder] = useState(null); // 클릭한 세그먼트(주문)
  // 캔버스를 이 리소스·버킷으로 스크롤/패닝시킨다. 매번 새 객체를 넣어 같은 대상 재선택도 동작하게 한다.
  const [revealTarget, setRevealTarget] = useState(null);

  const [menu, setMenu] = useState(null); // 우클릭 메뉴 { x, y, hit:{ resource, bucket } }
  const [zoom, setZoom] = useState(null); // 포커스 줌 { resource }

  // 기간 필터 단위 = 버전 버킷 단위(TIME_BUCKETS[0].UNIT). Month→월, Week→일(주 시작요일만), Day→일 전체.
  const bucketUnit = version?.planConfigObj?.TIME_BUCKETS?.[0]?.UNIT || CodeUtil.buckets.MONTH;
  // 주 시작요일 — 버전의 실제 계획 시작일(PLAN_START_DATE) 요일에서 도출한다.
  // 설정값 START_DAY가 실제 버킷 시작요일과 어긋나면(예: 버킷은 월요일 시작인데 설정은 다른 값)
  // 자동 채워진 종료일(주 마지막 요일)이 유효하지 않다고 빨갛게 표시되는 문제가 생겨서다.
  // PLAN_START_DATE 없으면 START_DAY → 월요일 순으로 폴백.
  const planStartDate = version?.planConfigObj?.PLAN_START_DATE;
  const startDay =
    (planStartDate && Object.values(CodeUtil.dayOfWeek).find((d) => d.id === new ZDate(planStartDate).getDay())?.cd) ||
    version?.planConfigObj?.START_DAY ||
    CodeUtil.dayOfWeek.MONDAY.cd;
  const isWeekBucket = CodeUtil.isWeekBucket(bucketUnit);
  const isDayBucket = String(bucketUnit).toLowerCase() === CodeUtil.buckets.DAY;
  const isMonthBucket = String(bucketUnit).toLowerCase() === CodeUtil.buckets.MONTH;
  const periodDateType = isWeekBucket || isDayBucket ? "day" : "month";
  // 주/일/월 버킷(또는 주/월 집계 표시)이면 연도 병합 행을 둔다.
  const yearRow = isWeekBucket || isDayBucket || isMonthBucket || !!displayBucket;
  // 월 병합 행 — 하단 라벨이 '월/일' 형태일 때만 의미가 있다. 월 버킷(또는 월 집계 표시)은
  // 하단 라벨 자체가 월이라 중복이므로 두지 않는다.
  const bottomIsMonth = isMonthBucket || displayBucket === CodeUtil.buckets.MONTH;
  const monthRow = yearRow && !bottomIsMonth;
  // 날짜 행은 그리드 헤더 높이(32) + 병합 행마다 22
  const headerHeight = 32 + (yearRow ? 22 : 0) + (monthRow ? 22 : 0);

  // day 버킷 헤더 요일 라벨(일~토) — lang-pack 적용(기존 sun~sat 키 재사용)
  const weekdayLabels = useMemo(() => ["sun", "mon", "tue", "wed", "thu", "fri", "sat"].map((k) => t(k)), [t]);
  // 월 병합 행 라벨(1~12월) — lang-pack 의 jan~dec 키 재사용
  const monthLabels = useMemo(
    () => ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"].map((k) => t(k)),
    [t]
  );

  // 포커스(1개 리소스) 시에는 버킷을 최대 높이로 크게 본다. 그 외엔 슬라이더 값.
  const effectiveRowHeight = zoom ? 320 : rowHeight;
  // 캔버스 렌더 옵션 — 값 변경 시에만 새 객체(불필요 재렌더 방지)
  const canvasOptions = useMemo(
    () => ({ rowHeight: effectiveRowHeight, bucketStandardCount: bucketCount, headerHeight, yearRow, monthRow }),
    [effectiveRowHeight, bucketCount, headerHeight, yearRow, monthRow]
  );

  // 뷰 옵션(표시 설정)을 viewName 상태에 저장 → 새로고침/탭 복귀 후에도 유지.
  // (필터 값 mpVersion/resource/period는 각 필터 컴포넌트가 같은 viewName에 merge 저장)
  useEffect(() => {
    reduxDispatch(
      reduxUtil.updateViewState(
        reduxUtil.createObjectState(viewName, {
          rowHeight,
          bucketCount,
          palette,
          stackDir,
          timeUom,
          colorMode,
          loadThresholds,
          itemSearch: itemSearch.current,
          overloadOnly,
          loadedOnly,
          showSetup,
          fitCapacity,
          displayBucket,
          hrchyLvCd,
          panelWidth,
        })
      )
    );
  }, [rowHeight, bucketCount, palette, stackDir, timeUom, colorMode, loadThresholds, itemSearch, overloadOnly, loadedOnly, showSetup, fitCapacity, displayBucket, hrchyLvCd, panelWidth, viewName, reduxDispatch]);

  // 품목 계층 레벨 목록(z_hrchy_config, itemHrchyFlg=true) — 계층 그룹 1번(hrchy_num=1)만 대상.
  // 레벨은 seq_num 로 구분(DW z_dim_item_hrchy 와 동일 규칙). 값=cd(레벨 코드), 라벨=name, 정렬=seqNum.
  useEffect(() => {
    hrchyConfigService
      .getAll({ searchFilters: { cd: null, itemHrchyFlg: true } })
      .then((res) => {
        const rows = res.data?.results ?? [];
        const levels = rows
          .filter((r) => Number(r?.hrchyNum) === 1 && r?.cd)
          .sort((a, b) => (a.seqNum ?? 0) - (b.seqNum ?? 0))
          .map((r) => ({ cd: r.cd, name: r.name }));
        setHrchyLevels(levels);
      })
      .catch((e) => debug(e));
  }, []);

  // 저장된 레벨 코드가 현재 옵션에 없으면(삭제/권한/데이터변경) 품목 모드로 자동 복귀한다.
  useEffect(() => {
    if (hrchyLvCd == null) return;
    if (hrchyLevels.length === 0) return;
    if (!hrchyLevels.some((l) => l.cd === hrchyLvCd)) {
      setHrchyLvCd(null);
    }
  }, [hrchyLvCd, hrchyLevels]);

  // 버전이 선택/변경되면 기간을 그 버전의 시작~종료일(planConfigObj)로 맞춘다.
  // 탭 복귀 시 MpVersionFilter 가 같은 버전을 새 객체로 다시 넣으므로, id 가 실제로 바뀐 경우에만 맞춘다
  // (그렇지 않으면 redux 에서 복원한 기간이 버전 기간으로 덮어써진다).
  const periodVersionIdRef = useRef(reduxViewState?.period && version?.id != null ? String(version.id) : null);
  useEffect(() => {
    const versionId = version?.id != null ? String(version.id) : null;
    if (versionId === periodVersionIdRef.current) return;
    periodVersionIdRef.current = versionId;
    const cfg = version?.planConfigObj;
    if (cfg?.PLAN_START_DATE && cfg?.PLAN_END_DATE) {
      setPeriod({ start: new ZDate(cfg.PLAN_START_DATE), end: new ZDate(cfg.PLAN_END_DATE) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);

  // 서버 조회 데이터 (version / resource / period 기준). 응답 = { resources, bucketsByResource, bucketKeys, bucketLabels }
  // 초기값 = redux(view-state, 메모리)에 캐싱된 직전 결과 → 탭 복귀 시 재조회 응답 전까지 빈 화면 대신 표시.
  const [data, setData] = useState(reduxViewState?.ganttData ?? EMPTY_GANTT);
  const [loading, setLoading] = useState(false); // 조회 중 스피너 표시

  // 조회 조건 시그니처 — 값이 같으면 중복 조회하지 않는다.
  // 아이템 검색(itemSearch)은 ref 라 여기에 넣어도 재계산되지 않으므로 제외한다.
  // 검색은 MPItemAutocomplete 의 retrieve(=runFetch) 로 직접 조회한다.
  const fetchKey = useMemo(() => {
    const rs = resource?.length ? resource.map((r) => r.resCd).sort() : null;
    const ps = period?.start ? new ZDate(period.start).getTime() : null;
    const pe = period?.end ? new ZDate(period.end).getTime() : null;
    // version.id 는 BigInt(json-bigint)일 수 있어 문자열로 변환(JSON.stringify가 BigInt 직렬화 불가)
    return JSON.stringify({ v: version?.id != null ? String(version.id) : null, r: rs, ps, pe, b: displayBucket || null, h: hrchyLvCd ?? null });
  }, [version, resource, period, displayBucket, hrchyLvCd]);

  const fetchTokenRef = useRef(0); // 최신 요청만 반영(레이스 방지)
  // 마지막으로 조회한 조건. 탭 복귀(마운트) 시엔 null 이라 캐시를 먼저 보여준 뒤 항상 재조회한다
  // (그 사이 MP 재실행 등으로 결과가 바뀌었을 수 있다).
  const lastFetchKeyRef = useRef(null);

  const runFetch = () => {
    if (!version?.id) {
      setData(EMPTY_GANTT);
      lastFetchKeyRef.current = null;
      return;
    }
    const token = ++fetchTokenRef.current;
    setLoading(true);
    resourceGanttService
      .getAll({
        versionId: version.id,
        resCd: resource?.length ? resource.map((r) => r.resCd) : undefined,
        periodStart: period?.start,
        periodEnd: period?.end,
        itemCd: itemSearch.current || undefined,
        bucketUom: displayBucket || undefined, // 표시 버킷 집계(week/month) — 서버가 버킷 병합
        hrchyLvCd: hrchyLvCd ?? undefined, // 품목 계층 '레벨'(z_hrchy_config.cd)로 세그먼트 집계
      })
      .then((res) => {
        if (token !== fetchTokenRef.current) return; // 오래된 응답 무시
        const gd = res.data ?? EMPTY_GANTT;
        setData(gd);
        setSelectedOrder(null);
        setHighlightOrder(null);
        setZoom(null);
        lastFetchKeyRef.current = fetchKey;
        // 결과를 redux(view-state)로 캐싱 → 복귀 시 즉시 표시(메모리 저장, localStorage 아님)
        // itemSearch 는 ref 라 뷰 옵션 effect 가 다시 돌지 않으므로, 조회 시점에 함께 저장한다.
        reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(viewName, { ganttData: gd, itemSearch: itemSearch.current || null })));
      })
      .catch((e) => debug(e))
      .finally(() => {
        if (token === fetchTokenRef.current) setLoading(false);
      });
  };

  // 마운트 시 + 필터가 바뀌면 조회. 같은 조건의 중복 호출(필터 컴포넌트가 같은 값을 새 객체로 다시 넣는 경우)은 생략.
  useEffect(() => {
    if (!version?.id) {
      setData(EMPTY_GANTT);
      lastFetchKeyRef.current = null;
      return;
    }
    if (lastFetchKeyRef.current === fetchKey) return;
    runFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey]);

  // 수동 새로고침 — 조건이 같아도 강제 재조회(백엔드 계획 결과 갱신 반영)
  const handleRefresh = () => runFetch();

  const base = data;
  // 전체 아이템 집합으로 색을 한 번 만들어 캔버스·패널이 동일 색을 쓰게 한다(팔레트 반영).
  const colorForItem = useMemo(
    () => buildItemColor(base.bucketsByResource, ITEM_PALETTES[palette] || ITEM_PALETTES[DEFAULT_PALETTE]),
    [base, palette]
  );
  // 부하율→색 (사용자 임계값 반영) — 캔버스(load 모드)·패널 칩 공유
  const loadColorFn = useMemo(() => makeLoadColor(loadThresholds), [loadThresholds]);

  // 패널 목록에서 다른 주문 선택 → 같은 버킷에서 item만 교체(캔버스 강조도 이동).
  // 그 세그먼트가 스크롤/패닝 밖으로 나가 있으면 화면을 그쪽으로 옮긴다 — 강조만 바뀌고
  // 정작 보이지 않으면 어디가 선택됐는지 알 수 없다.
  const handleSelectOrder = (item) => {
    if (!selectedOrder) return;
    setSelectedOrder({ ...selectedOrder, item });
    setRevealTarget({ resourceId: selectedOrder.resource?.id, bucketKey: selectedOrder.bucket?.key });
  };

  // 초과(어느 버킷이든 부하합 > capacity) 리소스 id 집합
  const overloadedSet = useMemo(() => {
    const s = new Set();
    for (const r of base.resources) {
      const buckets = base.bucketsByResource[r.id] || [];
      // 초과 판정 기준 = 한계 용량(총능력 × usageLimitRate)
      if (buckets.some((b) => isOverloaded(bucketUsed(b), bucketLimitCap(b)))) s.add(r.id);
    }
    return s;
  }, [base]);

  // 부하가 있는(어느 버킷이든 부하합 > 0) 리소스 id 집합 — 미가동 리소스 숨기기용
  const loadedSet = useMemo(() => {
    const s = new Set();
    for (const r of base.resources) {
      const buckets = base.bucketsByResource[r.id] || [];
      if (buckets.some((b) => bucketUsed(b) > 0)) s.add(r.id);
    }
    return s;
  }, [base]);

  // 현재 표시 데이터 — 초과만 / 미가동 숨김 필터 → 포커스 줌 순으로 좁힌다.
  const view = useMemo(() => {
    let resources = base.resources;
    if (overloadOnly) resources = resources.filter((r) => overloadedSet.has(r.id));
    if (loadedOnly) resources = resources.filter((r) => loadedSet.has(r.id));
    if (zoom) resources = resources.filter((r) => r.id === zoom.resource.id);
    return { ...base, resources };
  }, [zoom, base, overloadOnly, overloadedSet, loadedOnly, loadedSet]);

  // 포커스된 리소스의 버킷 — 응답은 데이터 없는 버킷을 생략하므로 bucketKeys 로 채워
  // 아래 차트/그리드의 열이 위 간트 x축과 어긋나지 않게 한다.
  const focusBuckets = useMemo(
    () => (zoom ? alignBuckets(view.bucketsByResource[zoom.resource.id] || [], view.bucketKeys) : []),
    [zoom, view]
  );

  // 방향키는 스크롤 대신 주문을 고른다 — 위/아래는 같은 버킷 안에서, 좌/우는 같은 리소스의 앞뒤 버킷으로.
  // 순서 기준은 패널 목록과 같다(세로 스택이면 items 를 뒤집은 것이 화면 위→아래 순서).
  useEffect(() => {
    if (!selectedOrder) return undefined; // 선택 전에는 기존 스크롤 동작 그대로

    const visualItems = (bucket) => {
      const items = bucket?.items ?? [];
      return stackDir === "vertical" ? [...items].reverse() : items;
    };

    // 데이터가 갱신되면 객체 동일성이 깨지므로 식별자로 비교한다(분할 주문은 조각 번호까지).
    const isSame = (a, b) =>
      a === b || (a?.orderNo === b?.orderNo && a?.item === b?.item && a?.orderSeqNum === b?.orderSeqNum);

    const handleKeyDown = (e) => {
      if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) return;
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return; // 검색창 등 입력 방해 금지
      e.preventDefault(); // 스크롤 대신 선택 이동

      const { resource, bucket } = selectedOrder;
      const list = visualItems(bucket);
      const at = list.findIndex((o) => isSame(o, selectedOrder.item));

      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        const next = list[at + (e.key === "ArrowDown" ? 1 : -1)];
        if (next) setSelectedOrder((prev) => (prev ? { ...prev, item: next } : prev));
        return;
      }

      // 좌/우 — 주문이 없는 버킷은 건너뛰고, 옮겨간 버킷에서는 같은 높이의 주문을 고른다.
      const buckets = view.bucketsByResource[resource.id] || [];
      const from = buckets.findIndex((b) => b.key === bucket.key);
      if (from < 0) return;
      const step = e.key === "ArrowRight" ? 1 : -1;
      for (let i = from + step; i >= 0 && i < buckets.length; i += step) {
        const nextList = visualItems(buckets[i]);
        if (!nextList.length) continue;
        setSelectedOrder({
          resource,
          bucket: buckets[i],
          item: nextList[Math.min(Math.max(at, 0), nextList.length - 1)],
        });
        setRevealTarget({ resourceId: resource?.id, bucketKey: buckets[i].key });
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedOrder, stackDir, view]);

  const handleColorMode = (_, next) => {
    if (next) setColorMode(next);
  };

  const handleContextMenu = (hit, clientX, clientY) => {
    setMenu({ x: clientX, y: clientY, hit });
  };

  // 패널의 주문 목록에서 우클릭 — 차트 세그먼트와 같은 메뉴를 띄운다.
  // hit 모양(resource/bucket/item)을 맞춰 넘기면 메뉴 항목들이 그대로 동작한다.
  const handleOrderContextMenu = (order, clientX, clientY) => {
    if (!selectedOrder) return;
    handleContextMenu({ resource: selectedOrder.resource, bucket: selectedOrder.bucket, item: order }, clientX, clientY);
  };

  const handleFocus = () => {
    setZoom({ resource: menu.hit.resource });
    setSelectedOrder(null);
    setMenu(null);
  };

  const handleHighlightOrder = () => {
    const orderNo = menu.hit.item?.orderNo ?? null;
    // 같은 주문을 다시 고르면 해제 — 메뉴 하나로 켜고 끈다(상단 Chip 의 X 와 같은 동작).
    setHighlightOrder((prev) => (prev != null && prev === orderNo ? null : orderNo));
    setMenu(null);
  };

  // 이 주문의 품목코드로 FLO 차트 이동(탭 열기/선택 + 이미 열린 탭이면 해당 품목으로 조회)
  const handleGoToFlo = () => {
    const code = menu?.hit?.item?.item;
    if (code) {
      const floState = reduxUtil.getViewState("MP_FLO_CHART") ?? {};
      reduxDispatch(
        reduxUtil.updateViewState(
          reduxUtil.createObjectState("MP_FLO_CHART", { ...floState, selectedItemCd: code, rootItemCd: code })
        )
      );
      window.dispatchEvent(new CustomEvent("plannel:floSearchItem", { detail: { itemCd: code } }));
      window.dispatchEvent(new CustomEvent("plannel:openMenu", { detail: { key: 4010 } })); // MP_FLO_CHART
    }
    setMenu(null);
  };

  // 이 세그먼트의 자원 위치(locCd) + 품목(또는 계층 모드면 리프 계층 카테고리) + 현재 버전으로 MP PSI Simulation 이동
  const handleGoToPsi = () => {
    const seg = menu?.hit?.item; // 계층 모드: orderNo=계층 코드, item=계층 이름 / 기본: item=품목코드
    const locCd = menu?.hit?.resource?.locCd;
    const locNm = menu?.hit?.resource?.group ?? locCd;
    const psiCond = {
      currentPage: 1,
      mpVersion: version, // 현재 보고 있는 버전 그대로
      filter: {
        locations: locCd ? [{ locCd, locNm }] : null,
        // 계층 집계 모드: 품목 대신 리프 계층(카테고리) 필터 → PSI params.categoryCds 로 조회
        category: groupByHrchy && seg?.orderNo ? [{ hrchyCd: seg.orderNo, name: seg.item, leafFlg: true }] : null,
      },
      itemSearch: groupByHrchy ? "" : seg?.item ?? "",
      extraItemCodes: [],
      // 계층 모드는 품목 타입이 혼합 → null(타입 필터 없음). 기본 모드는 그 품목의 타입(FG 기본에 SFG가 가려지지 않게).
      itemTypeSearch: groupByHrchy ? null : seg?.itemType ?? null,
      subGridResourceListFilter: [],
      appliedExceptions: [],
    };
    // 새로 열릴 때(마운트)는 viewState 로 초기 조회, 이미 열려 있으면(재마운트 X) 이벤트로 재조회.
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState("MP_PSI_SIMULATION", psiCond)));
    window.dispatchEvent(new CustomEvent("plannel:mpPsiSearch", { detail: psiCond }));
    window.dispatchEvent(new CustomEvent("plannel:openMenu", { detail: { key: 4102 } })); // MP_PSI_SIMULATION
    setMenu(null);
  };

  const zoomLabel = () => (zoom ? zoom.resource.name ?? zoom.resource.id : "");

  return (
    <>
      {/* 세로 중앙 정렬 — flex-end 면 짧은 아이콘 버튼이 키 큰 드롭다운 바닥에 붙어 줄이 어긋난다. */}
      <FilterContainer sx={{ pb: 1, alignItems: "center" }}>
        {/* 왼쪽 필터: 버전 · 기간(시작/종료) · S/M/H · 리소스 · 아이템 · 초과만 */}
        <MpVersionFilter viewName={viewName} mpVersionValue={version} onChangeFilter={setVersion} />
        <PeriodFilter
          viewName={viewName}
          period={period}
          setPeriod={setPeriod}
          dateType={periodDateType}
          shouldDisableStartDate={isWeekBucket ? (e) => WorkbenchUtil.ableDayOfWeek(e, bucketUnit, startDay) : undefined}
        />
        <ResourceFilter viewName={viewName} resourceValue={resource} onChangeFilter={setResource} isMultiSelect />
        {/* 아이템 코드 검색(Elasticsearch 자동완성 + 검색 아이콘) — 해당 아이템 + BOM 자식 생산 리소스만 */}
        <MPItemAutocomplete retrieve={runFetch} searchValue={itemSearch} key="ITEM" sx={{ width: 250 }} />
        {/* 표시 버킷 — 한 글자 토글(DP Workbench W/M/Q 스타일). W=주, M=월.
            아무것도 선택 안 함(재클릭으로 해제) = 버전 기본 버킷 그대로. */}
        <ToggleButtonGroup
          size="small"
          exclusive
          value={displayBucket}
          onChange={(_, v) => setDisplayBucket(v)}
          sx={{ mr: 1 }}
        >
          <StyledToggleButton value="week" sx={{ px: 1 }}>
            <Tooltip title={`${t("displayBucket")}: ${t("week")}`}>
              <span>W</span>
            </Tooltip>
          </StyledToggleButton>
          <StyledToggleButton value="month" sx={{ px: 1 }}>
            <Tooltip title={`${t("displayBucket")}: ${t("month")}`}>
              <span>M</span>
            </Tooltip>
          </StyledToggleButton>
        </ToggleButtonGroup>
        {/* 품목 계층 레벨로 집계 — 레벨 선택 시 세그먼트가 주문/품목 대신 그 레벨 계층 단위로 병합.
            옵션 라벨 = z_hrchy_config.name (레벨 드롭다운 컨벤션) */}
        <TextField
          select
          size="small"
          label={t("itemHrchy")}
          value={hrchyLvCd ?? ""}
          onChange={(e) => setHrchyLvCd(e.target.value === "" ? null : e.target.value)}
          sx={{ width: 150, mr: 1 }}
        >
          {hrchyLevels.map((l) => (
            <MenuItem key={l.cd} value={l.cd}>
              {l.name}
            </MenuItem>
          ))}
          {/* 품목(집계 없음)은 맨 아래 — 계층 레벨(상위→하위) 다음의 가장 세분화된 단위 */}
          <MenuItem value="">{t("item")}</MenuItem>
        </TextField>
        {/* 초과(>capacity) 리소스만 보기 · 미가동(부하 0) 리소스 숨기기 · 새로고침 — 테두리 없는 아이콘 버튼 */}
        <Tooltip title={t("overloadOnly")} placement="top">
          <IconButton size="small" color={overloadOnly ? "warning" : "default"} onClick={() => setOverloadOnly((v) => !v)}>
            <WarningAmberIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("hideIdleResources")} placement="top">
          <IconButton size="small" color={loadedOnly ? "primary" : "default"} onClick={() => setLoadedOnly((v) => !v)}>
            <HideSourceIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {/* 세그먼트 내 setup(작업 준비) 구간 표시/숨김 — capacity 에 포함된 값이라 막대 길이는 그대로고 색 구간만 사라진다 */}
        <Tooltip title={t("capaSetup")} placement="top">
          <IconButton size="small" color={showSetup ? "primary" : "default"} onClick={() => setShowSetup((v) => !v)}>
            <BuildOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {/* 아이콘은 DP Workbench 의 초기화 버튼(ResetButton, RotateLeft)에 맞춘다 — 두 화면이 같아 보이도록.
            단 이 버튼은 필터를 초기화하지 않고 현재 조건으로 재조회만 하므로 라벨/툴팁은 refresh 로 남긴다. */}
        <ResetButton label="refresh" onClick={handleRefresh} disabled={!version?.id || loading} />
        {/* 뷰 옵션(행 높이 · 버킷 폭 · 설정) — 우측 정렬(마지막 자식). 접기 아이콘과 세로 정렬 일치 */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Tooltip title={t("rowHeight")} placement="top">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: 150 }}>
              <HeightIcon fontSize="small" color="action" />
              <Slider
                size="small"
                min={240}
                max={1320}
                step={8}
                value={rowHeight}
                onChange={(_, v) => setRowHeight(v)}
                aria-label={t("rowHeight")}
              />
            </Box>
          </Tooltip>
          {/* 버킷(열) 너비 — 화면을 채우는 버킷 수(1~36). 반전 매핑: 오른쪽=넓게(버킷 수 적음). 기본 12 */}
          <Tooltip title={t("bucketWidth")} placement="top">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: 150 }}>
              <HeightIcon fontSize="small" color="action" sx={{ transform: "rotate(90deg)" }} />
              <Slider
                size="small"
                min={BUCKET_COUNT_MIN}
                max={BUCKET_COUNT_MAX}
                step={1}
                value={flipBucketCount(bucketCount)}
                onChange={(_, v) => setBucketCount(flipBucketCount(v))}
                aria-label={t("bucketWidth")}
              />
            </Box>
          </Tooltip>
          <SettingsButton onClick={(e) => setSettingsAnchor(e.currentTarget)} />
        </Box>
      </FilterContainer>
      {/* 상태 바 — 줌(전체 보기 복귀) / 같은 주문 강조(해제) */}
      {(zoom || highlightOrder) && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.5, bgcolor: "action.hover" }}>
          {zoom && (
            <>
              <Button size="small" startIcon={<ArrowBackIcon fontSize="small" />} onClick={() => setZoom(null)}>
                {t("zoomReset")}
              </Button>
              <Chip size="small" label={`${t("zoomInFocus")} · ${zoomLabel()}`} />
            </>
          )}
          {highlightOrder && (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={`${t("highlightSameOrder")} · ${highlightOrder}`}
              onDelete={() => setHighlightOrder(null)}
            />
          )}
        </Box>
      )}
      {/* 바깥 = 가로: [왼쪽 컬럼(간트/차트/그리드)] + [우측 패널(전체 높이)] */}
      <Box
        sx={{
          display: "flex",
          height: zoom || highlightOrder ? "calc(100% - 82px)" : "calc(100% - 50px)",
          overflow: "hidden",
        }}
      >
        {/* 왼쪽 컬럼 — 포커스 시 세로 스택(간트 → 차트 → 그리드) */}
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, minHeight: 0, overflow: "hidden" }}>
          {/* 간트: 포커스 시 최대 높이 한 행, 아니면 전체 채움 */}
          <Box sx={{ minHeight: 0, overflow: "hidden", position: "relative", ...(zoom ? { height: effectiveRowHeight + headerHeight + 10 } : { flex: 1 }) }}>
            <ResourceGanttCanvas
              resources={view.resources}
              bucketsByResource={view.bucketsByResource}
              bucketKeys={view.bucketKeys}
              bucketLabels={view.bucketLabels}
              laneHeaderLabel={t("resource")}
              emptyLabel={loading ? "" : t("noDataFoundLabel")}
              selected={selectedOrder}
              colorForItem={colorForItem}
              loadColorFn={loadColorFn}
              isolateOrderNo={highlightOrder}
              colorMode={colorMode}
              stackDir={stackDir}
              fitCapacity={fitCapacity}
              showSetup={showSetup}
              setupColor={getPaletteSetupColor(palette)}
              weekdayLabels={weekdayLabels}
              monthLabels={monthLabels}
              revealTarget={revealTarget}
              options={canvasOptions}
              onSegmentClick={setSelectedOrder}
              onBucketContextMenu={handleContextMenu}
            />
            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(255,255,255,0.5)",
                  zIndex: 5,
                }}
              >
                <CircularProgress />
              </Box>
            )}
          </Box>
          {zoom && (
            <>
              {/* 위 간트 캔버스가 자기 외곽선을 그리므로 여기서 borderTop 을 또 주면 2px 로 두꺼워진다. */}
              <Box sx={{ flex: "0 0 280px", minHeight: 0, p: 1, pb: 0 }}>
                <ResourceUtilChart
                  resource={zoom.resource}
                  buckets={focusBuckets}
                  bucketLabels={view.bucketLabels}
                  timeUom={timeUom}
                />
              </Box>
              <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", borderTop: "1px solid", borderColor: BORDER_COLOR }}>
                <ResourceUtilTable
                  buckets={focusBuckets}
                  bucketLabels={view.bucketLabels}
                  timeUom={timeUom}
                />
              </Box>
            </>
          )}
        </Box>
        {/* 우측 패널 — 전체 높이(포커스 여부 무관) */}
        <OrderSpecPanel
          hit={selectedOrder}
          bucketLabels={view.bucketLabels}
          colorForItem={colorForItem}
          loadColorFn={loadColorFn}
          timeUom={timeUom}
          stackDir={stackDir}
          width={panelWidth}
          onWidthChange={setPanelWidth}
          onSelectOrder={handleSelectOrder}
          onOrderContextMenu={handleOrderContextMenu}
          onClose={() => setSelectedOrder(null)}
        />
      </Box>

      <Menu
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={menu ? { top: menu.y, left: menu.x } : undefined}
      >
        <MenuItem onClick={handleFocus}>
          <CenterFocusStrongIcon fontSize="small" sx={{ mr: 1 }} />
          {t("zoomInFocus")}
        </MenuItem>
        {/* 계층 집계 모드에서는 세그먼트가 주문 단위가 아니라 '같은 주문 강조'를 숨긴다 */}
        {menu?.hit?.item && !groupByHrchy && (
          <MenuItem
            onClick={handleHighlightOrder}
            selected={highlightOrder != null && highlightOrder === menu?.hit?.item?.orderNo}
          >
            <HighlightAltIcon fontSize="small" sx={{ mr: 1 }} />
            {t("highlightSameOrder")}
          </MenuItem>
        )}
        {/* 계층 집계 모드에서는 세그먼트 item 이 품목코드가 아니라(계층) FLO 이동만 숨긴다.
            PSI 는 계층 모드에서도 카테고리(리프 계층) 필터로 이동 가능. */}
        {menu?.hit?.item && !groupByHrchy && isPermitted("MP_FLO_CHART") && (
          <MenuItem onClick={handleGoToFlo}>
            <AccountTreeIcon fontSize="small" sx={{ mr: 1 }} />
            {t("goToFlo")}
          </MenuItem>
        )}
        {menu?.hit?.item && isPermitted("MP_PSI_SIMULATION") && (
          <MenuItem onClick={handleGoToPsi}>
            <ScienceOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
            {t("menuMpPsiSimulation")}
          </MenuItem>
        )}
      </Menu>

      {/* ⚙ 설정 — 색상 기준 · 팔레트 · 부하 임계값 · 용량 단위 · 초과 여백 */}
      <Menu anchorEl={settingsAnchor} open={Boolean(settingsAnchor)} onClose={() => setSettingsAnchor(null)}>
        {/* 색상 기준 — 아이템별 / 부하율 */}
        <Typography variant="body2" sx={{ px: 2, display: "block", color: "text.primary" }}>
          {t("colorMode")}
        </Typography>
        <Box sx={{ px: 2, py: 1 }} onClick={(e) => e.stopPropagation()}>
          <ToggleButtonGroup size="small" exclusive value={colorMode} onChange={handleColorMode}>
            <StyledToggleButton value="item" sx={{ px: 1.25 }}>{t("colorByItem")}</StyledToggleButton>
            <StyledToggleButton value="load" sx={{ px: 1.25 }}>{t("colorByLoad")}</StyledToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* 간트 보는 방식 — 세로 스택(위아래) / 가로 스택(양옆) */}
        <Typography variant="body2" sx={{ px: 2, display: "block", color: "text.primary", borderTop: "1px solid", borderColor: "divider", pt: 0.5, mt: 0.5 }}>
          {t("ganttStackDirection")}
        </Typography>
        <Box sx={{ px: 2, py: 1 }} onClick={(e) => e.stopPropagation()}>
          <ToggleButtonGroup size="small" exclusive value={stackDir} onChange={(_, v) => v && setStackDir(v)}>
            <StyledToggleButton value="vertical" sx={{ px: 1.25, gap: 0.5 }}>
              <SwapVertIcon fontSize="small" />
              {t("floTopDown")}
            </StyledToggleButton>
            <StyledToggleButton value="horizontal" sx={{ px: 1.25, gap: 0.5 }}>
              <SwapHorizIcon fontSize="small" />
              {t("floLeftRight")}
            </StyledToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* 용량 표시 단위(초/분/시) — capacity 는 초 단위 저장. 캔버스 툴팁·부하 분석·주문 사양 패널에 함께 적용 */}
        <Typography variant="body2" sx={{ px: 2, display: "block", color: "text.primary", borderTop: "1px solid", borderColor: "divider", pt: 0.5, mt: 0.5 }}>
          {t("timeUom")}
        </Typography>
        <Box sx={{ px: 2, py: 1 }} onClick={(e) => e.stopPropagation()}>
          <ToggleButtonGroup size="small" exclusive value={timeUom} onChange={(_, v) => v && setTimeUom(v)}>
            <StyledToggleButton value="seconds" sx={{ px: 1.25 }}>{t("seconds")}</StyledToggleButton>
            <StyledToggleButton value="minutes" sx={{ px: 1.25 }}>{t("minutes")}</StyledToggleButton>
            <StyledToggleButton value="hour" sx={{ px: 1.25 }}>{t("hour")}</StyledToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Typography variant="body2" sx={{ px: 2, display: "block", color: "text.primary", borderTop: "1px solid", borderColor: "divider", pt: 0.5, mt: 0.5 }}>
          {t("colorPalette")}
        </Typography>
        {Object.entries(ITEM_PALETTES).map(([key, colors]) => (
          <MenuItem
            key={key}
            selected={key === palette}
            onClick={() => {
              setPalette(key);
              setSettingsAnchor(null);
            }}
            sx={{ display: "flex", gap: 1.5 }}
          >
            <CheckIcon fontSize="small" sx={{ visibility: key === palette ? "visible" : "hidden" }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2">{t(PALETTE_LABEL_KEY[key])}</Typography>
              <Box sx={{ display: "flex", gap: 0.25, mt: 0.5 }}>
                {colors.slice(0, 8).map((c, i) => (
                  <Box key={i} sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: c }} />
                ))}
              </Box>
            </Box>
          </MenuItem>
        ))}

        {/* 부하 색 임계값(%) — green < 노랑% ≤ 노랑 < 빨강% ≤ 빨강 */}
        <Box sx={{ borderTop: "1px solid", borderColor: "divider", mt: 0.5, pt: 0.5 }}>
          <Typography variant="body2" sx={{ px: 2, display: "block", color: "text.primary" }}>
            {t("loadColorScale")}
          </Typography>
          <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", gap: 1.5 }} onClick={(e) => e.stopPropagation()}>
            <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: LOAD_COLORS.tight }} />
            <TextField
              size="small"
              type="number"
              value={loadThresholds.yellow}
              onChange={(e) => setLoadThresholds((th) => ({ ...th, yellow: Number(e.target.value) }))}
              sx={{ width: 78 }}
              InputProps={{ endAdornment: <Typography variant="caption">%</Typography> }}
            />
            <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: LOAD_COLORS.over }} />
            <TextField
              size="small"
              type="number"
              value={loadThresholds.red}
              onChange={(e) => setLoadThresholds((th) => ({ ...th, red: Number(e.target.value) }))}
              sx={{ width: 78 }}
              InputProps={{ endAdornment: <Typography variant="caption">%</Typography> }}
            />
          </Box>
        </Box>

        {/* 초과 여백 숨김 — 켜면 100%일 때 버킷이 꽉 참(cap 위 여백 제거) */}
        <Box sx={{ borderTop: "1px solid", borderColor: "divider", mt: 0.5, pt: 0.5 }}>
          <MenuItem onClick={() => setFitCapacity((v) => !v)} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
            <Typography variant="body2">{t("hideOverloadSpace")}</Typography>
            <Switch size="small" edge="end" checked={fitCapacity} tabIndex={-1} />
          </MenuItem>
        </Box>
      </Menu>
    </>
  );
};

export default ResourceGantt;
